import { useState } from "react";

import { makePostApiCall, makeGetApiCall } from "../../shared/makeApiCall";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";

export function CampaignProductsDistributedViewModel(
  logout,
  campaignId,
  addSnackbar,
) {
  const [loading, setLoading] = useState(false);
  const [campaignVisits, setCampaignVisits] = useState([]);
  const [samplesDistributed, setSamplesDistributed] = useState([]);
  const [productsSold, setProductsSold] = useState([]);
  const [campaignInfo, setCampaignInfo] = useState(null);
  const [campaignTypesCount, setCampaignTypesCount] = useState({});
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const showErrorModal = (subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title="Error"
        />
      ),
      open: true,
    });
  };

  const showSnackbar = (message) => {
    addSnackbar(
      new SnackbarData(generateUniqueNumber(), message, SnackbarType.Success),
    );
  };

  // GET /fetchProductsByCampaignIdForDropdown/{id}
  const getCampaignProductsForDropdown = async function (campaign_id) {
    setLoading(true);
    try {
      const callResult = await makeGetApiCall(
        `fetchProductsByCampaignIdForDropdown/${campaign_id}`,
      );
      if (callResult.success) {
        return callResult.data;
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        } else {
          showErrorModal(
            callResult.data?.message ||
              "Could not load campaign products for dropdown",
          );
        }
        return [];
      }
    } finally {
      setLoading(false);
    }
  };

  // POST campaignProductsDistributed
  const getCampaignProductsDistributed = async function (campaign_id) {
    setLoading(true);
    try {
      const callResult = await makePostApiCall("campaignProductsDistributed", {
        campaign_id: campaign_id,
      });
      if (callResult.success) {
        setCampaignVisits(callResult.data.campaign_visits || []);
        setSamplesDistributed(callResult.data.samples_distributed || []);
        setProductsSold(callResult.data.products_sold || []);
        setCampaignInfo(callResult.data.campaign_info || null);
        setCampaignTypesCount(callResult.data.campaign_types_count || {});
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        } else {
          showErrorModal(
            callResult.data?.message ||
              "Could not load campaign products distributed",
          );
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // POST /api/campaign-products-distributed/create
  // type: 0 = Visit, 1 = Samples distributed, 2 = Products sold
  const addDistributedProduct = async function (campaign_id, type) {
    try {
      const callResult = await makePostApiCall(
        "addDistributedProductToCampaign",
        { campaign_id, type },
      );
      if (callResult.success) {
        showSnackbar(callResult.data?.message || "Record added successfully");

        if (type === 0) {
          setCampaignVisits((prev) => [callResult.data, ...prev]);
        } else if (type === 1) {
          setSamplesDistributed((prev) => [callResult.data, ...prev]);
        } else if (type === 2) {
          setProductsSold((prev) => [callResult.data, ...prev]);
        }

        // Increment counter for this type
        setCampaignTypesCount((prev) => {
          const key = String(type);
          const current = prev[key];
          return {
            ...prev,
            [key]: {
              type,
              count: (current?.count ?? 0) + 1,
            },
          };
        });

        console.log(callResult.data);

        return { success: true };
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        } else {
          showErrorModal(callResult.data?.error || "Could not add record");
        }
        return { success: false };
      }
    } catch (error) {
      showErrorModal("Could not add record");
      return { success: false };
    }
  };

  const endCampaign = async function (id) {
    const callResult = await makePostApiCall(`endCampaign/${id}`, {});
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message || "Campaign Ended successfully",
          SnackbarType.Success,
        ),
      );
      getCampaignProductsDistributed(campaignId);
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  // POST /api/campaign-products-distributed/delete/{id}
  const deleteDistributedProduct = async function (id, type) {
    try {
      const callResult = await makePostApiCall(
        `deleteDistributedCampaignProduct/${id}`,
        {},
      );
      if (callResult.success) {
        // Update local state based on type
        if (type === 0) {
          setCampaignVisits((prev) => prev.filter((item) => item.id !== id));
        } else if (type === 1) {
          setSamplesDistributed((prev) =>
            prev.filter((item) => item.id !== id),
          );
        } else if (type === 2) {
          setProductsSold((prev) => prev.filter((item) => item.id !== id));
        }

        // Decrement counter for this type
        setCampaignTypesCount((prev) => {
          const key = String(type);
          const current = prev[key];
          return {
            ...prev,
            [key]: {
              type,
              count: Math.max(0, (current?.count ?? 0) - 1),
            },
          };
        });

        setEmptyModelOpen({ open: false, childComponent: null });
        showSnackbar(callResult.data?.message || "Record deleted successfully");
        return { success: true };
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        } else {
          showErrorModal(callResult.data?.message || "Could not delete record");
        }
      }
    } catch (error) {
      showErrorModal("Could not delete record");
    }
  };

  return {
    addDistributedProduct,
    campaignInfo,
    campaignVisits,
    campaignTypesCount,
    deleteDistributedProduct,
    emptyModal,
    getCampaignProductsDistributed,
    getCampaignProductsForDropdown,
    loading,
    productsSold,
    samplesDistributed,
    setEmptyModelOpen,
    endCampaign,
  };
}
